const { elements } = require('../variables');
const { installmentsNotAvailable } = require('../html');

/**
 * Starts the pre-payment availability check for a BNPL (Buy Now Pay Later)
 * flex payment method, without touching the DOM.
 *
 * The check is deliberately kicked off before the payment method blocks are
 * rendered so the network round-trip overlaps with the rest of the render.
 * The result is applied later by {@link applyFlexPaymentMethod}, once the
 * loading block is guaranteed to be mounted (#159853).
 *
 * Supports: Tamara, Tabby, Taly
 *
 * @param {Object} Selector - Selector instance for DOM operations
 * @param {Object} configs - Payment method configuration
 * @param {Object} options - Method-specific options
 * @param {Function} [options.onInit] - Method-specific initialization function
 * @param {Function} [options.checkAvailability] - Function to check if method is available
 * @param {Function} [options.getLabel] - Called with (pre_payment_options, configs) to get the method label
 * @param {Function} [options.onError] - Error reporter, called with (error, context)
 * @param {Function} options.getWidget - Function to generate widget HTML
 * @returns {Object|null} Handle to pass to applyFlexPaymentMethod, or null when
 *                        no pre-payment check is needed
 */
const startFlexPaymentMethod = (Selector, configs, options = {}) => {
  if (!configs) return null;

  const { code, pre_payment_check_url, pre_payment_options_available } = configs;

  // Handle method-specific initialization
  if (options.onInit) {
    options.onInit(configs);
  }

  if (pre_payment_options_available) return null;

  return {
    configs,
    options,
    optionsPromise: Selector.getinstallmentsOptions(pre_payment_check_url, code),
  };
};

/**
 * Applies the result of a pre-payment check to its payment method block.
 * Must be called after the flex method blocks have been rendered.
 *
 * Never rejects: a missing block or a failed check is reported through
 * options.onError so it cannot fail silently.
 *
 * @param {Object} documentCheckout - Shadow DOM checkout element
 * @param {Object|null} handle - Handle returned by startFlexPaymentMethod
 * @returns {Promise<void>}
 */
const applyFlexPaymentMethod = (documentCheckout, handle) => {
  if (!handle) return Promise.resolve();

  const { configs, options } = handle;
  const { flow, icon } = configs;

  const reportError = (error) => {
    console.error('BNPL pre-payment check failed for ' + flow + ':', error);
    if (options.onError) {
      options.onError(error, { flow, code: configs.code });
    }
  };

  return handle.optionsPromise
    .then((pre_payment_options) => {
      const methodBlock = documentCheckout.querySelector(`[data-method-id="id_${flow}"]`);

      if (!methodBlock) {
        throw new Error(
          `BNPL method block [data-method-id="id_${flow}"] is not mounted`
        );
      }

      // Check availability based on method-specific logic
      const isAvailable = options.checkAvailability
        ? options.checkAvailability(pre_payment_options)
        : pre_payment_options?.is_available;

      if (isAvailable) {
        const methodLabel = options.getLabel
          ? options.getLabel(pre_payment_options, configs)
          : '';
        methodBlock.innerHTML = options.getWidget(methodLabel, configs);
        methodBlock.classList.add(elements.accordionItem);
        methodBlock.classList.remove(elements.disabled, elements.loadingInstallmentsOptionsBlock);
      } else {
        methodBlock.innerHTML = installmentsNotAvailable(icon, flow);
        methodBlock.classList.remove(
          elements.accordionItem,
          elements.paymentMethod,
          elements.loadingInstallmentsOptionsBlock,
          'ottu__sdk-methods-theme',
          'ottu__sdk-redirect-links-theme',
          'ottu__sdk-border-green'
        );
      }
    })
    .catch(reportError);
};

/**
 * Generic handler for BNPL flex payment methods: starts the pre-payment check
 * and applies its result in one step. Kept for callers that render the method
 * block before the check begins.
 *
 * @returns {Promise<void>}
 */
const handleFlexPaymentMethod = (Selector, documentCheckout, configs, options = {}) =>
  applyFlexPaymentMethod(documentCheckout, startFlexPaymentMethod(Selector, configs, options));

exports.handleFlexPaymentMethod = handleFlexPaymentMethod;
exports.startFlexPaymentMethod = startFlexPaymentMethod;
exports.applyFlexPaymentMethod = applyFlexPaymentMethod;
